import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CircleHelp, Menu, Search, Sparkles, UsersRound } from 'lucide-react'
import { Menu as MuiMenu, MenuItem, Tooltip } from '@mui/material'
import V3ClientSelect from './V3ClientSelect'
import { useV3ShellLayout } from './V3ShellLayoutContext'

type Props = {
  currentUser?: any
  clientCode?: string
  isStaff?: boolean
  isAffiliate?: boolean
  onOpenAskIndie?: () => void
  onToggleNav?: () => void
  onReplayTour?: () => void
  showNavToggle?: boolean
  periodSelector?: React.ReactNode
}

function getTrialDaysRemaining(user: any) {
  const endDate = user?.trial_ends_at || user?.trialEndsAt
  if (!endDate) return null
  const remaining = Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000)
  return Number.isFinite(remaining) ? Math.max(0, remaining) : null
}

export default function V3PortalHeader({
  currentUser,
  clientCode,
  isStaff = false,
  isAffiliate = false,
  onOpenAskIndie,
  onToggleNav,
  onReplayTour,
  showNavToggle = false,
  periodSelector,
}: Props) {
  const navigate = useNavigate()
  const { leftNavWidth } = useV3ShellLayout()
  const [query, setQuery] = React.useState('')
  const [helpAnchor, setHelpAnchor] = React.useState<HTMLElement | null>(null)
  const trialDays = getTrialDaysRemaining(currentUser)
  const headerRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    const header = headerRef.current
    if (!header) return undefined

    const updateChromeBottom = () => {
      document.documentElement.style.setProperty('--portal-chrome-bottom', `${header.getBoundingClientRect().bottom}px`)
    }
    window.addEventListener('resize', updateChromeBottom)
    updateChromeBottom()

    return () => {
      window.removeEventListener('resize', updateChromeBottom)
      document.documentElement.style.removeProperty('--portal-chrome-bottom')
    }
  }, [])

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const value = query.trim()
    if (!value) return
    navigate(`/v3/portal/search?q=${encodeURIComponent(value)}`)
  }

  const replayTour = () => {
    setHelpAnchor(null)
    onReplayTour?.()
    window.dispatchEvent(new CustomEvent('orbit:replay-tour', { detail: { path: window.location.pathname } }))
  }

  return (
      <header ref={headerRef} className="sticky top-0 z-40 flex h-auto min-h-14 min-w-0 flex-wrap items-center gap-4 border-b border-[#EAECF0] bg-white px-4 min-[1280px]:h-14 min-[1280px]:flex-nowrap sm:px-8" style={{ fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', marginLeft: leftNavWidth || undefined }}>
        {showNavToggle && (
          <button
            type="button"
            aria-label="Open navigation menu"
            onClick={onToggleNav}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#EAECF0] text-[#344054] hover:bg-[#F9FAFB]"
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
        <form role="search" onSubmit={submitSearch} className={`relative flex min-w-0 max-w-[560px] flex-1 items-center ${showNavToggle ? 'basis-[calc(100%-52px)]' : ''} min-[1000px]:min-w-[150px]`}>
          <Search aria-hidden="true" className="pointer-events-none absolute left-[13px] top-1/2 h-4 w-4 -translate-y-1/2 text-[#7A7168]" />
          <input
            aria-label="Search discussions, articles, podcasts by keywords"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search discussions, articles, podcasts by keywords"
            className="h-10 min-w-0 w-full rounded-[10px] border border-[#EAECF0] bg-white py-[9px] pl-[38px] pr-[34px] text-[14px] text-[#171717] outline-none placeholder:text-[#9D958C] focus:border-[#F97215] focus:ring-[3px] focus:ring-[#FFE4CC]"
          />
        </form>
        {isStaff && (
          <V3ClientSelect className="hidden min-[1000px]:block w-[180px] shrink-0" placeholder={clientCode || 'Select a client'} />
        )}
        {periodSelector && <div className="shrink-0">{periodSelector}</div>}
        {trialDays !== null && (
          <span className="inline-flex shrink-0 items-center text-[13px] text-[#475467]">{trialDays} days</span>
        )}
        <button
          type="button"
          onClick={() => navigate('/v3/portal/billing')}
          className="inline-flex h-9 shrink-0 items-center rounded-full bg-[#C2410C] px-2 sm:px-3 text-[13px] font-semibold text-white hover:bg-[#9A3412]"
        >
          Upgrade
        </button>
        {isAffiliate && !showNavToggle && (
          <button type="button" aria-label="Coaches' Corner" onClick={() => navigate('/v3/portal/coaches-corner')} className="inline-flex h-9 w-9 min-[480px]:w-auto shrink-0 items-center justify-center gap-1.5 rounded-md text-[13px] font-medium text-[#475467] hover:bg-[#F9FAFB] min-[480px]:px-2">
            <UsersRound aria-hidden="true" className="h-4 w-4" />
            <span className="hidden min-[480px]:inline">Coaches' Corner</span>
          </button>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            aria-label="Ask Indie"
            onClick={() => onOpenAskIndie ? onOpenAskIndie() : navigate(clientCode ? `/v3/client/${encodeURIComponent(clientCode)}/indie-ai` : '/v3/home')}
            className="inline-flex h-9 w-[126px] shrink-0 items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#4F46E5_0%,#7F56D9_100%)] px-4 text-[14px] font-semibold text-white shadow-[0_1px_1.5px_rgba(0,0,0,.10)]"
          >
            <Sparkles aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span>Ask Indie</span>
          </button>
          <Tooltip title="Help" placement="bottom" enterDelay={0} disableInteractive componentsProps={{ tooltip: { sx: { fontSize: 13, fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif' } } }}>
          <button
            type="button"
            aria-label="Help"
            aria-controls={helpAnchor ? 'portal-help-menu' : undefined}
            aria-expanded={helpAnchor ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={(event) => setHelpAnchor(event.currentTarget)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D0D5DD] text-[#475467] hover:bg-[#F9FAFB]"
          >
            <CircleHelp aria-hidden="true" className="h-[17px] w-[17px]" />
          </button>
          </Tooltip>
          <MuiMenu
            id="portal-help-menu"
            anchorEl={helpAnchor}
            open={Boolean(helpAnchor)}
            onClose={() => setHelpAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 214, p: '6px', border: '1px solid #EAECF0', borderRadius: '10px', boxShadow: '0 12px 28px rgba(16,24,40,.14)' } } }}
          >
            <MenuItem component={Link} to="/v3/portal/help?product=indie" onClick={() => setHelpAnchor(null)} sx={{ height: 36, borderRadius: '8px', fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}>Help - Indie Insights</MenuItem>
            <MenuItem component={Link} to="/v3/portal/help?product=retail" onClick={() => setHelpAnchor(null)} sx={{ height: 36, borderRadius: '8px', fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}>Help - RetailORBIT</MenuItem>
            <MenuItem component={Link} to="/v3/portal/help?product=support" onClick={() => setHelpAnchor(null)} sx={{ height: 36, borderRadius: '8px', fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}>Submit a Ticket</MenuItem>
            <MenuItem onClick={replayTour} sx={{ height: 36, borderRadius: '8px', fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif', fontSize: 14, color: '#344054' }}>Launch Page Tour</MenuItem>
          </MuiMenu>
        </div>
      </header>
  )
}
